<template>
    <Teleport to="body">
        <transition name="fade">
            <div v-if="isTooltipVisible"
                 :class="`mobile-tooltip-wrapper ${name}-tooltip`"
                 :style="`height: ${ windowHeigth }px`">
                <div class="overlay d-flex align-items-end"
                     @click="closeTooltip">
                    <div class="content"
                         @click.stop>
                        <slot />
                        <div v-if="defaultButton"
                             class="close-button-wrapper">
                            <BaseButton @click="closeTooltip">
                                <Translate translationKey="quests-game.gotIt"
                                           fallback="Got it" />
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script setup lang="ts">
    import {
        computed, ref, onMounted, onBeforeUnmount,
    } from 'vue';
    import { useTooltipStore } from '@/js/store/quests/game/modules/TippyMobileStore';
    import { EventTypeEnum } from '@/js/enums/enums';
    import BaseButton from '@/js/components/BaseButton.vue';

    interface Props {
        name: string;
        defaultButton?: boolean;
    }
    const props = withDefaults(defineProps<Props>(), {
        defaultButton: true,
    });

    const tooltipStore = useTooltipStore();
    const isTooltipVisible = computed(() => tooltipStore.tooltips[props.name]);
    const closeTooltip = () => {
        tooltipStore[`set${props.name.charAt(0).toUpperCase()}${props.name.slice(1)}Tooltip`](false);
    };

    const windowHeigth = ref(window.innerHeight);

    const onResize = () => {
        windowHeigth.value = window.innerHeight;
    };

    onMounted(() => {
        window.addEventListener(EventTypeEnum.RESIZE, onResize);
    });

    onBeforeUnmount(() => {
        window.removeEventListener(EventTypeEnum.RESIZE, onResize);
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/missions-partials/z-index" as missionsZIndex;
@use "@/sass/partials/breakpoints" as breakpoints;

.mobile-tooltip-wrapper {
    display: none;
}

@media (max-width: breakpoints.$md-breakpoint-max-width) {
    .mobile-tooltip-wrapper {
        position: fixed;
        z-index: missionsZIndex.$tippy-mobile-z-index;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: block;
        width: 100%;

        .overlay {
            width: 100%;
            height: 100%;
            background-color: rgba(21, 11, 38, 0.8);
        }

        .content {
            width: 100%;
            padding: 16px;
            background-color: legacyVariables.$tippy-mobile-background;
            border-top-left-radius: 16px;
            border-top-right-radius: 16px;
            color: legacyVariables.$white;
            font-size: 16px;
            font-weight: 500;
        }
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .fade-enter-to,
    .fade-leave-from {
        opacity: 1;
    }

    .close-button-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 24px;

        button {
            margin-bottom: 4px;
        }
    }
}
</style>
